import { useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useSelector } from "react-redux"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { FaFlag } from "react-icons/fa"
import type {
  EmployeeOnboardingDetailsDto,
  EmployeeFieldFlagDto,
} from "@/modules/employee/employee/dto/employeeDetails"
import {
  bankDetailsSchema,
  type BankDetailsFormData,
} from "@/modules/employee/employee/schemas/employeeFormSchemas"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import type { RootState } from "@/store"
import { sage300ConnectorService } from "@/modules/profile/infrastructure/sage300ConnectorService"
import type { Sage300Bank } from "@/modules/profile/dtos/Sage300ConnectDto"

const KNOWN_BANKS = [
  { name: "ABSA Bank", branchCode: "632005" }, //
  { name: "African Bank", branchCode: "430000" },
  { name: "Bidvest Bank", branchCode: "462005" },
  { name: "Capitec Bank", branchCode: "470010" },
  { name: "Citi Bank", branchCode: "350005" },
  { name: "First National Bank", branchCode: "250655" },
  { name: "Investec Bank", branchCode: "580105" },
  { name: "Nedbank", branchCode: "198765" },
  { name: "Standard Bank", branchCode: "051001" },
] as const

const CUSTOM_VALUE = "__custom__"

interface BankDetailsFormProps {
  formData: EmployeeOnboardingDetailsDto["bankDetails"]
  onUpdate: (bank: EmployeeOnboardingDetailsDto["bankDetails"]) => void
  isReadOnly?: boolean
  fieldFlags?: EmployeeFieldFlagDto[]
}

export function BankDetailsForm({
  formData,
  onUpdate,
  isReadOnly = false,
  fieldFlags,
}: BankDetailsFormProps) {
  const bankDetails = useMemo(
    () =>
      typeof formData === "string"
        ? {
            accountHolderName: "",
            bankName: "",
            accountNumber: "",
            branchCode: "",
            accountType: "",
            accountRelationship: "",
          }
        : formData,
    [formData],
  )

  const organizationId = useSelector((s: RootState) =>
    selectCurrentOrganizationId(s),
  )

  const [sageBanks, setSageBanks] = useState<Sage300Bank[] | null>(null)
  const [sageBankCode, setSageBankCode] = useState("")
  const [isFetchingSageBanks, setIsFetchingSageBanks] = useState(false)

  const isKnownBank = (name: string) => KNOWN_BANKS.some((b) => b.name === name)

  const form = useForm<BankDetailsFormData>({
    resolver: zodResolver(bankDetailsSchema),
    defaultValues: {
      accountHolderName: bankDetails.accountHolderName,
      bankName: bankDetails.bankName,
      accountNumber: bankDetails.accountNumber,
      branchCode: bankDetails.branchCode,
      accountType: bankDetails.accountType ?? "",
      accountRelationship: bankDetails.accountRelationship ?? "",
    },
    mode: "onChange",
  })

  useEffect(() => {
    const subscription = form.watch((values) => {
      onUpdate(values as EmployeeOnboardingDetailsDto["bankDetails"])
    })
    return () => subscription.unsubscribe()
  }, [form, form.watch, onUpdate])

  // Coerce nullish → "" on both sides so null-vs-empty-string from the API
  // doesn't loop reset → watch → onUpdate.
  useEffect(() => {
    const s = (v: unknown) => (v ?? "") as string
    const current = form.getValues()
    const hasExternalChange =
      s(current.accountHolderName) !== s(bankDetails.accountHolderName) ||
      s(current.bankName) !== s(bankDetails.bankName) ||
      s(current.accountNumber) !== s(bankDetails.accountNumber) ||
      s(current.branchCode) !== s(bankDetails.branchCode) ||
      s(current.accountType) !== s(bankDetails.accountType) ||
      s(current.accountRelationship) !== s(bankDetails.accountRelationship)

    if (!hasExternalChange) return

    form.reset({
      accountHolderName: s(bankDetails.accountHolderName),
      bankName: s(bankDetails.bankName),
      accountNumber: s(bankDetails.accountNumber),
      branchCode: s(bankDetails.branchCode),
      accountType: s(bankDetails.accountType),
      accountRelationship: s(bankDetails.accountRelationship),
    })
    form.trigger()

    if (sageBanks) {
      const match = sageBanks.find(
        (b) => b.description === bankDetails.bankName,
      )
      setSageBankCode(match?.code ?? "")
    }
  }, [bankDetails, form, sageBanks])

  useEffect(() => {
    if (!organizationId) return
    setIsFetchingSageBanks(true)
    sage300ConnectorService
      .getBanks(organizationId)
      .then((banks) => setSageBanks(banks))
      .catch(() => setSageBanks(null))
      .finally(() => setIsFetchingSageBanks(false))
  }, [organizationId])

  // Initialise sageBankCode once Sage banks load by matching the stored bank name.
  const formRef = useRef(form)
  formRef.current = form
  useEffect(() => {
    if (!sageBanks) return
    const match = sageBanks.find(
      (b) => b.description === formRef.current.getValues("bankName"),
    )
    setSageBankCode(match?.code ?? "")
  }, [sageBanks])

  const sectionFlag = fieldFlags?.find(
    (f) => f.fieldName === "bank" && !f.resolvedAt,
  )
  const isSectionFlagged = !!sectionFlag
  const isFieldDisabled = isReadOnly && !isSectionFlagged

  const isSageMode = !isFetchingSageBanks && sageBanks !== null

  const uniqueSageBanks = useMemo(() => {
    if (!sageBanks) return []
    const seen = new Map<string, Sage300Bank>()
    for (const b of sageBanks) {
      if (!seen.has(b.code)) seen.set(b.code, b)
    }
    return [...seen.values()]
  }, [sageBanks])

  const sageBranchOptions = useMemo(() => {
    if (!sageBanks || !sageBankCode) return []
    return sageBanks.filter((b) => b.code === sageBankCode)
  }, [sageBanks, sageBankCode])

  const watchedBankName = form.watch("bankName")
  const selectedKnownBank = KNOWN_BANKS.find((b) => b.name === watchedBankName)

  const handleBankSelect = (value: string) => {
    if (value === CUSTOM_VALUE) {
      form.setValue("bankName", "", { shouldValidate: true })
      form.setValue("branchCode", "", { shouldValidate: true })
    } else {
      const bank = KNOWN_BANKS.find((b) => b.name === value)
      if (bank) {
        form.setValue("bankName", bank.name, { shouldValidate: true })
        form.setValue("branchCode", bank.branchCode, { shouldValidate: true })
      }
    }
  }

  const bankSelectValue = isKnownBank(watchedBankName)
    ? watchedBankName
    : CUSTOM_VALUE

  return (
    <Form {...form}>
      <form className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">Bank Details</h2>

        {isSectionFlagged && (
          <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
            <FaFlag className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium mb-1">
                This section needs to be updated:
              </p>
              <p className="text-orange-700">{sectionFlag.note}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="accountHolderName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Account Holder Name *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Bank selector — Sage 300 mode or KNOWN_BANKS fallback */}
          {isSageMode ? (
            <FormItem>
              <FormLabel>Bank *</FormLabel>
              <Select
                value={sageBankCode}
                onValueChange={(code) => {
                  const bank = uniqueSageBanks.find((b) => b.code === code)
                  if (bank) {
                    setSageBankCode(code)
                    form.setValue("bankName", bank.description, {
                      shouldValidate: true,
                    })
                    form.setValue("branchCode", "", { shouldValidate: true })
                  }
                }}
                disabled={isFieldDisabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a bank" />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {uniqueSageBanks.map((b) => (
                    <SelectItem
                      key={b.code}
                      value={b.code}
                      className="hover:bg-slate-200"
                    >
                      {b.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          ) : (
            <>
              <FormItem>
                <FormLabel>Bank *</FormLabel>
                <Select
                  value={bankSelectValue}
                  onValueChange={handleBankSelect}
                  disabled={isFieldDisabled || isFetchingSageBanks}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        isFetchingSageBanks ? "Loading…" : "Select a bank"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    {KNOWN_BANKS.map((b) => (
                      <SelectItem
                        key={b.name}
                        value={b.name}
                        className="hover:bg-slate-200"
                      >
                        {b.name}
                      </SelectItem>
                    ))}
                    <SelectItem
                      value={CUSTOM_VALUE}
                      className="hover:bg-slate-200"
                    >
                      Other (enter manually)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>

              {/* Show manual bank name input only when "Other" is selected */}
              {bankSelectValue === CUSTOM_VALUE && (
                <FormField
                  control={form.control}
                  name="bankName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bank Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isFieldDisabled}
                          placeholder="Enter bank name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </>
          )}

          <FormField
            control={form.control}
            name="accountNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Account Number *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) =>
                      field.onChange(e.target.value.replace(/\D/g, ""))
                    }
                    disabled={isFieldDisabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isSageMode ? (
            <FormField
              control={form.control}
              name="branchCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Branch *</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isFieldDisabled || !sageBankCode}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            sageBankCode
                              ? "Select branch"
                              : "Select a bank first"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-card">
                      {sageBranchOptions.map((b) => (
                        <SelectItem
                          key={b.branchCode}
                          value={b.branchCode}
                          className="hover:bg-slate-200"
                        >
                          {b.branchName} — {b.branchCode}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <FormField
              control={form.control}
              name="branchCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Branch Code *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase())
                      }
                      disabled={isFieldDisabled || !!selectedKnownBank}
                      className={
                        selectedKnownBank
                          ? "bg-muted text-muted-foreground"
                          : ""
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="accountType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Account Type *</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isFieldDisabled}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select account type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-card">
                    <SelectItem value="Cheque" className="hover:bg-slate-200">
                      Cheque
                    </SelectItem>
                    <SelectItem value="Savings" className="hover:bg-slate-200">
                      Savings
                    </SelectItem>
                    <SelectItem
                      value="Transmission"
                      className="hover:bg-slate-200"
                    >
                      Transmission
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="accountRelationship"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Account Relationship *</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isFieldDisabled}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select account relationship" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-card">
                    <SelectItem value="Own" className="hover:bg-slate-200">
                      Own Account
                    </SelectItem>
                    <SelectItem
                      value="ThirdParty"
                      className="hover:bg-slate-200"
                    >
                      Third Party
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  )
}